import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'; // Importa useRef
import io from 'socket.io-client'; // Importa la librería Socket.IO client

// URL de tu API Backend de Flask
const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
 
// Lista completa de oficinas registrales
const ALL_OFICINAS = [
    'Artigas', 'Canelones', 'Cerro Largo', 'Colonia', 'Durazno',
    'Flores', 'Florida', 'Lavalleja', 'Maldonado', 'Montevideo',
    'Paysandú', 'Río Negro', 'Rivera', 'Rocha', 'Salto',
    'San José', 'Soriano',
    'Tacuarembó', 'Treinta y Tres',
    'La Costa', 'Pando'
];

// Lista de tipos de registro con sus valores reg_planca
const ALL_REGISTROS_OPTIONS = [
    { label: 'Registro Nacional de Actos Personales', value: 'RGI' },
    { label: 'Registro de la Propiedad, Sección Inmobiliaria', value: 'RPI' },
    { label: 'Registro de la Propiedad, Sección Mobiliaria, Aeronaves', value: 'RAE' },
    { label: 'Registro de la Propiedad, Sección Mobiliaria, Automotores', value: 'RA' },
    { label: 'Registro de la Propiedad, Sección Mobiliaria, Prenda S/Despl.', value: 'PSD' },
    { label: 'Registro de Personas Jurídicas', value: 'RCO' },
    { label: 'Registro de Personas Jurídicas Sec. Asoc. Civ. y Fundaciones', value: 'ACF' },
    { label: 'Registro de Rúbrica de Libros', value: 'RUB' }
];

// Lista de estados considerados "finales" para el filtro de pendientes
const FINAL_ESTADOS = ['admitida', 'definitivo', 'baja', 'cumplida', 'caduca', 'rechazada'];

// Componente principal de la aplicación
const App = () => {
    // Estados de autenticación
    const [token, setToken] = useState(localStorage.getItem('app_token'));
    const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem('app_user')));

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterEstado, setFilterEstado] = useState('');
    const [filterOficina, setFilterOficina] = useState('');
    const [sortColumn, setSortColumn] = useState('ingresado'); // Changed default sort column to 'ingresado'
    const [sortDirection, setSortDirection] = useState('desc');
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [editedRecord, setEditedRecord] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // Initialize with null
    const [actionMessage, setActionMessage] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [newRecordData, setNewRecordData] = useState({});
    const [newRecordErrors, setNewRecordErrors] = useState({}); // CORREGIDO: Ahora es un estado de React
    const [activeFilterType, setActiveFilterType] = useState('all'); // 'all', 'pending', 'debt', 'estado'

    // Nuevos estados para las notificaciones
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);

    // Ref para almacenar referencias a las filas de la tabla
    const rowRefs = useRef({});

    // Función para cerrar el menú de notificaciones al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Verifica si se hizo clic fuera del contenedor del icono de notificación
            if (showNotifications && event.target.closest('.notification-container') === null) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNotifications]);

    // Efecto para cargar las notificaciones desde localStorage al montar el componente
    useEffect(() => {
        try {
            console.log('[DEBUG localStorage] Intentando cargar notificaciones...');
            const storedNotifications = localStorage.getItem('appNotifications');
            if (storedNotifications) {
                // Parse notifications and ensure 'timestamp' is a Date object and 'data' is an object
                const parsedNotifications = JSON.parse(storedNotifications).map(n => ({
                    ...n,
                    timestamp: new Date(n.timestamp),
                    data: n.data || {} // Ensure data is an object, even if it was null/undefined
                }));
                setNotifications(parsedNotifications);
                console.log('[DEBUG localStorage] Notificaciones cargadas:', parsedNotifications);
            } else {
                console.log('[DEBUG localStorage] No se encontraron notificaciones en localStorage.');
            }
        } catch (e) {
            console.error("Error al cargar notificaciones desde localStorage:", e);
        }
    }, []);

    // Efecto para guardar las notificaciones en localStorage cada vez que cambian
    useEffect(() => {
        if (notifications.length > 0) { // Solo guardar si hay notificaciones
            try {
                localStorage.setItem('appNotifications', JSON.stringify(notifications));
                console.log('[DEBUG localStorage] Notificaciones guardadas:', notifications);
            } catch (e) {
                console.error("Error al guardar notificaciones en localStorage:", e);
            }
        } else {
            // Si las notificaciones quedan vacías, limpiar localStorage para evitar datos obsoletos
            localStorage.removeItem('appNotifications');
            console.log('[DEBUG localStorage] Notificaciones eliminadas de localStorage.');
        }
    }, [notifications]);


    // Función para realizar peticiones (sin autenticación)
    const simpleFetch = useCallback(async (url, options = {}, isLogin = false) => {
        const currentToken = localStorage.getItem('app_token');
        const headers = {
            ...options.headers,
        };

        // Añadir el token de autorización a todas las peticiones, excepto al login
        if (currentToken && !isLogin) {
            headers['Authorization'] = `Bearer ${currentToken}`;
        }


        // Si el método no es GET y no hay un Content-Type ya definido, usar application/json
        if (options.method && options.method !== 'GET' && !headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }

        try {
            const response = await fetch(`${API_URL}${url}`, { ...options, headers });
            if (!response.ok) {
                let errorMessage = `Error HTTP! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorData.msg || errorMessage;
                } catch (jsonError) {
                    errorMessage = response.statusText || errorMessage;
                }
                // Si el error es 401 (No autorizado), desloguear al usuario
                if (response.status === 401) {
                    console.error("Token no válido o expirado. Deslogueando...");
                    handleLogout(); // Llamar a la función de logout
                    setError("Tu sesión ha expirado. Por favor, inicia sesión de nuevo.");
                }

                throw new Error(errorMessage);
            }
            return response;
        } catch (err) {
            console.error("Error en la petición:", err);
            setError("Error de red o servidor. Por favor, intente de nuevo: " + err.message);
            return Promise.reject(err);
        }
    }, []);

    // Efecto para cargar los datos de los registros desde la API backend al iniciar la aplicación
    // Este useEffect solo se ejecuta una vez al montar el componente
    useEffect(() => {
        const fetchInitialRecords = async () => { // Solo se ejecuta si hay un token
            setLoading(true);
            setError(null);
            try {
                const response = await simpleFetch('/api/gestiones');
                const data = await response.json();
                if (Array.isArray(data)) {
                    setRecords(data);
                    console.log('[DEBUG Frontend] Registros actualizados en el estado:', data); // Nuevo log para depuración
                } else {
                    console.error("API did not return an array for records:", data);
                    setError("La API no devolvió una lista de registros válida.");
                    setRecords([]);
                }
            } catch (err) {
                console.error("Error al obtener registros de la API backend:", err);
                setRecords([]);
            } finally {
                setLoading(false);
            }
        };
        // Solo intentar cargar los registros si el usuario está logueado
        if (token) {
            fetchInitialRecords();
        }
    }, [simpleFetch, token]); // Se vuelve a ejecutar si el token cambia (al hacer login)

    // Maneja el clic en una fila para expandir/colapsar y cargar para edición
    const handleRowClick = useCallback((record) => {
        if (expandedRowId === record.id) {
            setExpandedRowId(null);
            setIsEditing(false);
            setEditedRecord(null); // Colapsar: establecer a null
        } else {
            // Expandir: establecer editedRecord primero, luego expandedRowId
            setEditedRecord({ ...record });
            setExpandedRowId(record.id);
            setIsEditing(false);
        }
    }, [expandedRowId]);


    // Function to mark a specific notification as read and expand the corresponding row
    const handleNotificationClick = useCallback((notificationId, recordId) => {
        setNotifications(prevNotifs =>
            prevNotifs.map(n => (n.id === notificationId ? { ...n, read: true } : n))
        );
        if (recordId) {
            const recordToExpand = records.find(rec => rec.id === recordId);
            if (recordToExpand) {
                // 1. Set editedRecord immediately.
                setEditedRecord({ ...recordToExpand });
                setIsEditing(false);

                // 2. Then, set the expanded row. This will trigger the useEffect for scrolling and the conditional rendering.
                setExpandedRowId(recordToExpand.id);

                // 3. Clear filters last.
                setSearchTerm('');
                setFilterEstado('');
                setFilterOficina('');
                setActiveFilterType('all');
            }
        }
        setShowNotifications(false); // Close the notification dropdown
    }, [records]); // 'records' is a dependency because we need to find 'recordToExpand'

    // Nueva función para eliminar una notificación específica
    const handleDeleteNotification = (e, notificationIdToDelete) => {
        e.stopPropagation(); // Evita que se active el handleNotificationClick del div padre
        setNotifications(prevNotifs => prevNotifs.filter(n => n.id !== notificationIdToDelete));
    };

    // New useEffect for scrolling
    useEffect(() => {
        if (expandedRowId) {
            const targetRow = rowRefs.current[expandedRowId];
            if (targetRow) {
                // Desplazamiento inmediato para una mejor experiencia de usuario
                targetRow.scrollIntoView({ behavior: 'smooth', block: 'start' });
                console.log(`[DEBUG Scroll] Scrolled to record ID: ${expandedRowId} immediately.`);
            } else {
                console.warn(`[DEBUG Scroll] Ref not found for expandedRowId: ${expandedRowId}. Retrying with a small delay.`);
                // Fallback: Si la referencia no está disponible inmediatamente, reintentar con un pequeño retraso
                const retryTimer = setTimeout(() => {
                    const retryTargetRow = rowRefs.current[expandedRowId];
                    if (retryTargetRow) {
                        retryTargetRow.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        console.log(`[DEBUG Scroll] Scrolled to record ID: ${expandedRowId} after retry.`);
                    } else {
                        console.error(`[DEBUG Scroll] Ref still not found for expandedRowId: ${expandedRowId} after retry.`);
                    }
                }, 10); // Un retraso muy pequeño como fallback
                return () => clearTimeout(retryTimer);
            }
        }
    }, [expandedRowId]); // Depende de expandedRowId


    // Este useEffect se encarga solo de la conexión y escucha de Socket.IO
    useEffect(() => {
        // Configura la conexión de Socket.IO
        // Solo conectar si hay un token
        if (!token) {
            return;
        }

        // Usamos una ruta relativa para que Nginx la maneje correctamente.
        const socket = io(API_URL, {
            path: '/socket.io/',
        });

        // Eventos de depuración de Socket.IO
        socket.on('connect', () => {
            console.log('[✅ SocketIO] Conectado al servidor de Socket.IO');
        });

        socket.on('disconnect', (reason) => {
            console.log(`[❌ SocketIO] Desconectado del servidor de Socket.IO. Razón: ${reason}`);
        });

        socket.on('connect_error', (err) => {
            console.error(`[❌ SocketIO] Error de conexión con Socket.IO: ${err.message}`);
            setError(`No se pudo conectar al servidor en tiempo real. Verifique que el backend y el proxy (Nginx) estén ejecutándose correctamente.`);
        });
        // Escucha el evento 'records_changed' del servidor para actualizaciones granulares
        socket.on('records_changed', (data) => {
            console.log('[📡 SocketIO] Datos actualizados recibidos via Socket.IO:', data);
            const { action, record, id } = data;

            setRecords(prevRecords => {
                let newRecords = [...prevRecords];

                switch (action) {
                    case 'add':
                        newRecords = [record, ...newRecords];
                        break;

                    case 'update':
                        const oldRecord = prevRecords.find(rec => rec.id === record.id);
                        let stateChanged = false;
                        let observationChanged = false;
                        let planchaChanged = false;
                        let hasPlanchaNow = false;

                        if (oldRecord) {
                            // Detectar cambio de estado
                            if (oldRecord.estado !== record.estado) {
                                stateChanged = true;
                            }

                            // Detectar cambio de observación
                            const oldObs = (oldRecord.observacion || '').trim();
                            const newObs = (record.observacion || '').trim();
                            if (oldObs !== newObs) {
                                observationChanged = true;
                            }

                            // ✅ Detectar si la plancha fue descargada (pasó de vacía a tener valor)
                            const oldPlancha = oldRecord.plancha;
                            const newPlancha = record.plancha;
                            if (!oldPlancha && newPlancha) {
                                planchaChanged = true;
                                hasPlanchaNow = true;
                            }
                        }

                        // ✅ Notificar si cambió estado, observación o ahora hay plancha
                        if (stateChanged || observationChanged || planchaChanged) {
                            setNotifications(prevNotifs => {
                                // Deduplicación: evita notificaciones repetidas en menos de 1.5 segundos
                                const isDuplicateNotification = prevNotifs.some(n =>
                                    n.recordId === record.id &&
                                    n.type === 'update' &&
                                    (Date.now() - n.timestamp.getTime() < 1500)
                                );

                                if (isDuplicateNotification) {
                                    console.warn('[DEBUG NOTIFICATION] Notificación duplicada (frontend) descartada para ID:', record.id);
                                    return prevNotifs;
                                }

                                console.log('[DEBUG NOTIFICATION] Añadiendo notificación de actualización:', record.id);
                                return [
                                    {
                                        id: Date.now() + Math.random(),
                                        recordId: record.id,
                                        read: false,
                                        timestamp: new Date(),
                                        type: 'update',
                                        data: {
                                            nro: record.nro,
                                            denominacion_reservada: record.denominacion_reservada,
                                            stateChanged,
                                            oldEstado: oldRecord ? oldRecord.estado : null,
                                            newEstado: record.estado,
                                            observationChanged,
                                            planchaChanged,        // ✅ Nuevo: indica que hubo cambio en plancha
                                            hasPlancha: hasPlanchaNow // ✅ Nuevo: indica que ahora hay plancha
                                        }
                                    },
                                    ...prevNotifs
                                ];
                            });
                        }

                        newRecords = newRecords.map(rec =>
                            rec.id === record.id ? record : rec
                        );
                        break;

                    case 'delete':
                        newRecords = newRecords.filter(rec => rec.id !== id);
                        break;

                    default:
                        console.warn('Acción de Socket.IO desconocida:', action);
                        return prevRecords;
                }

                return newRecords; // El ordenamiento se manejará por el useMemo de filteredAndSortedRecords
            });
        });

        // Limpia la conexión de Socket.IO cuando el componente se desmonta
        return () => {
            console.log('[🧹 SocketIO] Desconectando Socket.IO...');
            socket.disconnect();
        };
    }, [token]); // Se vuelve a conectar si el token cambia (al hacer login)

    // Define las columnas que se mostrarán en la tabla y su orden
    const TABLE_COLUMNS = useMemo(() => [
        { key: 'nro', label: 'Número', width: '12%' }, // Increased width further
        { key: 'denominacion_reservada', label: 'Denominación Reservada', width: '16%' }, // Adjusted width
        { key: 'oficina_registral', label: 'Oficina Registral', width: '9%' }, // Adjusted width
        { key: 'acto', label: 'Acto', width: '16%' }, // Adjusted width
        { key: 'ingresado', label: 'Ingresado', width: '9%' },
        { key: 'anio', label: 'Año', width: '8%' },
        { key: 'estado', label: 'Estado', width: '8%' },
        { key: 'escribano', label: 'Escribano', width: '22%' }, // Increased width further
    ], []);

    // Define los campos que se mostrarán en la vista de detalles expandida y su orden
    const DETAIL_FIELDS = useMemo(() => [
        'registro', 'nro', 'anio', 'pin', 'bis',
        'oficina_registral', 'denominacion_reservada',
        'deuda', 'vence', 'monto', 'ult_update', 'fecha_desc_planca', 'escribano', 'observacion' // Swapped 'escribano' and 'fecha_desc_planca'
    ], []);

    // Manejador para cambiar la columna de ordenación y la dirección
    const handleSort = (columnKey) => {
        if (sortColumn === columnKey) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    // Memoiza los registros filtrados y ordenados para evitar recálculos innecesarios
    const filteredAndSortedRecords = useMemo(() => {
        let currentRecords = [...records];

        // Aplicar filtro basado en activeFilterType primero
        if (activeFilterType === 'pending') {
            currentRecords = currentRecords.filter(r => r.estado && !FINAL_ESTADOS.includes(r.estado.toLowerCase()));
        } else if (activeFilterType === 'debt') {
            currentRecords = currentRecords.filter(r => r.deuda && r.deuda.toLowerCase().includes('deuda'));
        } else if (activeFilterType === 'estado' && filterEstado) {
            currentRecords = currentRecords.filter(record =>
                record.estado && record.estado.toLowerCase() === filterEstado.toLowerCase()
            );
        }

        // Aplicar término de búsqueda y filtro de oficina *después* del tipo de filtro primario
        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            currentRecords = currentRecords.filter(record => {
                const nro = String(record.nro || '').toLowerCase();
                const denominacion = String(record.denominacion_reservada || '').toLowerCase();
                return nro.includes(lowerCaseSearchTerm) || denominacion.includes(lowerCaseSearchTerm);
            });
        }

        if (filterOficina) {
            currentRecords = currentRecords.filter(record =>
                record.oficina_registral && record.oficina_registral.toLowerCase() === filterOficina.toLowerCase()
            );
        }

        currentRecords.sort((a, b) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            // 1. Manejo de valores nulos/indefinidos primero para cualquier tipo de columna
            if (aValue === null || aValue === undefined) {
                return bValue === null || bValue === undefined ? 0 : (sortDirection === 'asc' ? 1 : -1);
            }
            if (bValue === null || bValue === undefined) {
                return sortDirection === 'asc' ? -1 : 1;
            }

            // 2. Manejo de fechas
            if (sortColumn === 'ingresado' || sortColumn === 'ult_update' || sortColumn === 'fecha_desc_planca' || sortColumn === 'vence') {
                const dateA = new Date(aValue);
                const dateB = new Date(bValue);

                // Si alguna fecha es inválida, se maneja como un valor no comparable
                if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
                if (isNaN(dateA.getTime())) return sortDirection === 'asc' ? 1 : -1;
                if (isNaN(dateB.getTime())) return sortDirection === 'asc' ? -1 : 1;

                // CORRECCIÓN: Usar dateA.getTime() en lugar de a.getTime()
                return sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
            }

            // 3. Manejo de cadenas (después de asegurar que no son nulas/indefinidas)
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            // 4. Manejo de números y otros tipos comparables (después de asegurar que no son nulos/indefinidas y no son cadenas)
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0; // Si son iguales o no se pueden comparar
        });

        return currentRecords;
    }, [records, searchTerm, filterEstado, filterOficina, sortColumn, sortDirection, activeFilterType]);

    // Obtiene una lista de estados únicos para el filtro desplegable
    const uniqueEstados = useMemo(() => {
        // Asegurarse de que records sea un array antes de intentar mapear
        if (!Array.isArray(records)) {
            return ['', 'Sin calificar']; // Devolver una lista vacía predeterminada si records no es un array
        }
        const estados = new Set(records.map(record => record.estado).filter(Boolean));
        return ['', 'Sin calificar', ...Array.from(estados).filter(s => s !== 'Sin calificar').sort()];
    }, [records]);

    // Obtiene una lista de oficinas únicas para el filtro desplegable
    const uniqueOficinas = useMemo(() => {
        return ['', ...ALL_OFICINAS.sort()];
    }, []);

    // Calcula el resumen de estados para la sección de resumen
    const stateSummary = useMemo(() => {
        // Asegurarse de que records sea un array antes de intentar iterar
        if (!Array.isArray(records)) {
            return {}; // Devolver un objeto vacío si records no es un array
        }
        const summary = {};
        records.forEach(record => {
            const estado = record.estado || 'Sin calificar';
            summary[estado] = (summary[estado] || 0) + 1;
        });
        return Object.keys(summary).sort().reduce((obj, key) => {
            obj[key] = summary[key];
            return obj;
        }, {});
    }, [records]); // Añadir records a las dependencias

    // Maneja el cambio en los campos del formulario de edición
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Manejo especial para 'registro' y 'reg_planca' al editar
        if (name === 'registro') {
            const selectedOption = ALL_REGISTROS_OPTIONS.find(option => option.label === value);
            setEditedRecord(prev => ({
                ...prev,
                registro: value, // Mantener la etiqueta para 'registro'
                reg_planca: selectedOption ? selectedOption.value : '' // Actualizar 'reg_planca' basado en la etiqueta seleccionada
            }));
        } else if (name === 'reg_planca') {
             const selectedOption = ALL_REGISTROS_OPTIONS.find(option => option.value === value);
             setEditedRecord(prev => ({
                 ...prev,
                 reg_planca: value, // Mantener el valor para 'reg_planca'
                 registro: selectedOption ? selectedOption.label : '' // Actualizar 'registro' basado en el valor seleccionado
             }));
        } else {
            setEditedRecord(prev => ({ ...prev, [name]: value }));
        }
    };

    // Maneja el clic en el botón de editar
    const handleEditClick = () => {
        setIsEditing(true);
    };

    // Maneja el guardado de cambios
    const handleSave = async () => {
        let message = '¿Estás seguro de que quieres guardar los cambios?';
        const originalRecord = records.find(rec => rec.id === editedRecord.id);

        if (originalRecord) {
            if (originalRecord.estado !== editedRecord.estado) {
                message += `\n- El estado ha cambiado de "${originalRecord.estado || 'N/A'}" a "${editedRecord.estado || 'N/A'}".`;
            }
            if (originalRecord.observacion !== editedRecord.observacion) {
                message += `\n- La observación ha cambiado de "${originalRecord.observacion || 'N/A'}" a "${editedRecord.observacion || 'N/A'}".`;
            }
        }

        setActionMessage(message);
        setConfirmAction(() => async () => {
            try {
                const response = await simpleFetch(`/api/gestiones/${editedRecord.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(editedRecord),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    setActionMessage(`Error al guardar el registro: ${errorData.error || response.statusText}`);
                    setConfirmAction(null);
                    setShowConfirmModal(true);
                    throw new Error(errorData.error || `Error al guardar: ${response.statusText}`);
                }

                setExpandedRowId(null);
                setIsEditing(false);
                setEditedRecord(null);
                // No es necesario llamar a fetchRecords() aquí, Socket.IO se encargará de ello.
                setError(null);

                setActionMessage('Registro actualizado exitosamente.');
                setConfirmAction(null);
                setShowConfirmModal(true);

            } catch (err) {
                console.error("Error al guardar el registro:", err);
            }
        });
        setShowConfirmModal(true);
    };

    // Maneja la eliminación del registro
    const handleDelete = async () => {
        setActionMessage('¿Estás seguro de que quieres eliminar este registro? Esta acción es irreversible.');
        setConfirmAction(() => async () => {
            try {
                const response = await simpleFetch(`/api/gestiones/${expandedRowId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    setActionMessage(`Error al eliminar el registro: ${errorData.error || response.statusText}`);
                    setConfirmAction(null);
                    setShowConfirmModal(true);
                    throw new Error(errorData.error || `Error al eliminar: ${response.statusText}`);
                }

                setExpandedRowId(null);
                setIsEditing(false);
                setEditedRecord(null);
                // No es necesario llamar a fetchRecords() aquí, Socket.IO se encargará de ello.
                setError(null);

                setActionMessage('Registro eliminado exitosamente.');
                setConfirmAction(null);
                setShowConfirmModal(true);

            } catch (err) {
                console.error("Error al eliminar el registro:", err);
            }
        });
        setShowConfirmModal(true);
    };

    // Maneja la apertura del formulario para agregar un nuevo registro
    const openAddForm = () => {
        const currentYear = new Date().getFullYear();
        setNewRecordData({
            nro: '',
            registro: '', // Esto almacenará la etiqueta
            reg_planca: '', // Esto almacenará el valor
            oficina_registral: '',
            anio: currentYear.toString(),
            bis: '0',
            estado: 'Sin calificar',
            denominacion_reservada: '',
            pin: '',
            ingresado: '',
        });
        setNewRecordErrors({}); // CORREGIDO: Limpiar errores al abrir el formulario
        setShowAddForm(true); // Muestra el formulario
    };

    // Maneja el cierre del formulario para agregar un nuevo registro
    const closeAddForm = () => {
        setShowAddForm(false); // Oculta el formulario
        setNewRecordData({});
        setNewRecordErrors({}); // CORREGIDO: Limpiar errores al cerrar el formulario
        setError(null);
    };

    // Maneja el cambio en los campos del formulario de añadir nuevo registro
    const handleNewRecordInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'registro') { // 'registro' es el nombre del input select
            const selectedOption = ALL_REGISTROS_OPTIONS.find(option => option.value === value);
            setNewRecordData(prev => ({
                ...prev,
                registro: selectedOption ? selectedOption.label : '', // Almacenar la etiqueta para la columna 'registro'
                reg_planca: selectedOption ? selectedOption.value : '' // Almacenar el valor para la columna 'reg_planca'
            }));
        } else {
            setNewRecordData(prev => ({ ...prev, [name]: value }));
        }
        // Limpiar el error para este campo si el usuario comienza a escribir
        if (newRecordErrors[name]) { // CORREGIDO: Ahora newRecordErrors es un estado, se puede leer
            setNewRecordErrors(prevErrors => ({ ...prevErrors, [name]: false })); // CORREGIDO: Ahora se puede actualizar
        }
    };

    // Maneja el envío del formulario para agregar un nuevo registro
    const handleAddRecordSubmit = async () => {
        let currentErrors = {};
        let hasErrors = false;

        // Validar 'nro', 'oficina_registral', 'denominacion_reservada', 'estado' directamente
        // Para 'registro' (que ahora controla 'reg_planca'), validar 'reg_planca'
        const requiredFields = ['nro', 'reg_planca', 'oficina_registral', 'denominacion_reservada', 'estado'];

        requiredFields.forEach(field => {
            if (!newRecordData[field] || String(newRecordData[field]).trim() === '') {
                // Para 'reg_planca', marcar 'registro' como el campo de error para mostrar
                currentErrors[field === 'reg_planca' ? 'registro' : field] = true;
                hasErrors = true;
            } else {
                currentErrors[field === 'reg_planca' ? 'registro' : field] = false;
            }
        });

        setNewRecordErrors(currentErrors); // CORREGIDO: Ahora se actualiza el estado de errores

        if (hasErrors) {
            // Si hay errores, no se muestra el modal de confirmación, solo se resaltan los campos
            return;
        }

        setActionMessage('¿Estás seguro de que quieres agregar este nuevo registro?');
        setConfirmAction(() => async () => {
            try {
                const response = await simpleFetch(`/api/gestiones`, {
                    method: 'POST',
                    body: JSON.stringify(newRecordData),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    setActionMessage(`Error al agregar el registro: ${errorData.error || response.statusText}`);
                    setConfirmAction(null);
                    setShowConfirmModal(true);
                    throw new Error(errorData.error || `Error al agregar: ${response.statusText}`);
                }

                closeAddForm(); // Oculta el formulario y vuelve al resumen
                // No es necesario llamar a fetchRecords() aquí, Socket.IO se encargará de ello.
                setError(null);

                setActionMessage('Registro agregado exitosamente.');
                setConfirmAction(null);
                setShowConfirmModal(true);

            } catch (err) {
                console.error("Error al agregar el registro:", err);
            }
        });
        setShowConfirmModal(true);
    };

    // Función de utilidad para formatear cadenas de fecha para una visualización amigable
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            let date;
            // Verificar si dateString es solo un formato YYYY-MM-DD
            if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // Parsear año, mes, día y crear un objeto Date en la hora local
                const [year, month, day] = dateString.split('-').map(Number);
                // El mes en JavaScript Date es indexado desde 0
                date = new Date(year, month - 1, day);
            } else {
                // Para otros formatos de cadena de fecha (ej. con hora y zona horaria),
                // recurrir al análisis de fecha predeterminado.
                date = new Date(dateString);
            }

            // Asegurarse de que sea una fecha válida antes de formatear
            if (isNaN(date.getTime())) {
                return dateString; // Fallback si el análisis falla
            }

            // Formato '23 de julio 2025'
            const options = { day: 'numeric', month: 'long', year: 'numeric' };
            return date.toLocaleDateString('es-ES', options);
        } catch {
            return dateString; // Fallback para cualquier otro error
        }
    };

    // Función para obtener la clase de color de Tailwind CSS basada en el estado
    const getBadgeColorClass = (estado) => {
        if (!estado) return 'bg-gray-100 text-gray-800';
        const lowerCaseEstado = estado.toLowerCase();
        switch (lowerCaseEstado) {
            case 'admitida':
                return 'bg-green-100 text-green-800';
            case 'cumplida':
                return 'bg-blue-100 text-blue-800';
            case 'rechazada':
                return 'bg-red-100 text-red-800';
            case 'en tramite':
                return 'bg-yellow-100 text-yellow-800';
            case 'observada':
                return 'bg-orange-100 text-orange-800';
            case 'caduca':
                return 'bg-gray-200 text-gray-800';
            case 'definitivo':
                return 'bg-indigo-100 text-indigo-800';
            case 'sin calificar':
                return 'bg-gray-400 text-gray-900';
            default:
                return 'bg-purple-100 text-purple-800';
        }
    };

    // Helper function para formatear etiquetas para mostrar
    const formatLabel = (key) => {
        const labels = {
            'registro': 'Registro',
            'nro': 'Número',
            'anio': 'Año',
            'bis': 'Bis',
            'acto': 'Acto',
            'estado': 'Estado',
            'denominacion_reservada': 'Denominación Reservada',
            'pin': 'Pin',
            'deuda': 'Deuda',
            'fecha_desc_planca': 'Desc. Plancha',
            'reg_planca': 'Reg. Plancha',
            'vence': 'Vencimiento Deuda', // Etiqueta añadida para 'vence'
            'monto': 'Monto Deuda',      // Etiqueta añadida para 'monto'
            'observacion': 'Observación',
            'escribano': 'Escribano',
            'ruta_deuda': 'Ruta Deuda PDF',
            'ruta_screenshot': 'PDF Observación',
            'ult_update': 'Ult. Act.'
        };
        return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Función para obtener la clase de color de Tailwind CSS para los datos dinámicos en las notificaciones
    const getNotificationDataColorClass = (type) => {
        switch (type) {
            case 'add':
                return 'text-green-700';
            case 'update': // Ahora un solo tipo para todas las actualizaciones
                return 'text-blue-700'; // Azul genérico para actualizaciones
            case 'delete':
                return 'text-red-700';
            default:
                return 'text-gray-700';
        }
    };

    // Maneja el clic en los botones de resumen de estado
    const handleStateFilterClick = (estado) => {
        setFilterEstado(estado);
        setSearchTerm(''); // Limpiar otros filtros
        setFilterOficina(''); // Limpiar otros filtros
        setActiveFilterType('estado'); // Establecer el tipo de filtro activo
    };

    // Nuevas funciones para los filtros de "Pendientes" y "Deuda"
    const handlePendingFilterClick = () => {
        setFilterEstado(''); // Limpiar filtro de estado específico
        setSearchTerm(''); // Limpiar término de búsqueda
        setFilterOficina(''); // Limpiar filtro de oficina
        setActiveFilterType('pending'); // Establecer el tipo de filtro activo a 'pending'
    };

    const handleDebtFilterClick = () => {
        setFilterEstado(''); // Limpiar filtro de estado específico
        setSearchTerm(''); // Limpiar término de búsqueda
        setFilterOficina(''); // Limpiar filtro de oficina
        setActiveFilterType('debt'); // Establecer el tipo de filtro activo a 'debt'
    };

    // Maneja el clic en el botón de Total de Registros para limpiar filtros
    const handleClearFilters = () => {
        setSearchTerm('');
        setFilterEstado('');
        setFilterOficina('');
        setActiveFilterType('all'); // Establecer el tipo de filtro activo a 'all'
    };

    const handleDownload = useCallback(async (fileType) => {
        if (!editedRecord) {
            console.error("No hay un registro seleccionado para la descarga.");
            setActionMessage("Por favor, seleccione un registro para descargar el archivo.");
            setConfirmAction(null);
            setShowConfirmModal(true);
            return;
        }

        const configMap = {
            plancha: {
                urlKey: 'plancha',
                fallbackFilename: `plancha_${editedRecord.nro}_${editedRecord.denominacion_reservada || 'sin_nombre'}.pdf`
            },
            pdf_deuda: {
                urlKey: 'ruta_deuda',
                fallbackFilename: `deuda_${editedRecord.nro}_${editedRecord.denominacion_reservada || 'sin_nombre'}.pdf`
            },
            screenshot: {
                urlKey: 'ruta_screenshot',
                fallbackFilename: `observacion_${editedRecord.nro}_${editedRecord.denominacion_reservada || 'sin_nombre'}.pdf`
            }
        };

        const config = configMap[fileType];

        if (!config) {
            console.error(`Configuración de descarga no encontrada para el tipo: ${fileType}`);
            setError(`No se encontró la configuración para descargar el tipo de archivo: ${fileType}.`);
            return;
        }

        if (!editedRecord[config.urlKey]) {
            setActionMessage(`No hay un archivo de ${fileType.replace(/_/g, ' ')} para este registro.`);
            setConfirmAction(null);
            setShowConfirmModal(true);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await simpleFetch(`/api/gestiones/${editedRecord.id}/${fileType}`);
            const blob = await response.blob();
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            
            const contentDisposition = response.headers.get('Content-Disposition');
            let filenameFromHeader = null;
            if (contentDisposition) {
                const matches = /filename="([^"]*)"/.exec(contentDisposition);
                if (matches && matches[1]) {
                    filenameFromHeader = decodeURIComponent(matches[1].replace(/\+/g, ' '));
                }
            }
            
            a.download = filenameFromHeader || config.fallbackFilename;
            
            document.body.appendChild(a);
            a.click();
            
            window.URL.revokeObjectURL(url);
            a.remove();

        } catch (err) {
            console.error(`Error al descargar el archivo de ${fileType}:`, err);
            setError(`Error al descargar el archivo. Por favor, intente de nuevo.`);
        } finally {
            setLoading(false);
        }
    }, [editedRecord, simpleFetch]);

    // --- Funciones de Login/Logout ---
    const handleLoginSuccess = (newToken, user) => {
        localStorage.setItem('app_token', newToken);
        localStorage.setItem('app_user', JSON.stringify(user));
        setToken(newToken);
        setCurrentUser(user);
        setError(null); // Limpiar errores al hacer login
    };

    const handleLogout = () => {
        localStorage.removeItem('app_token');
        localStorage.removeItem('app_user');
        setToken(null);
        setCurrentUser(null);
        setRecords([]); // Limpiar registros al hacer logout
    };

    // Si no hay token, mostrar el componente de Login
    if (!token) {
        return <Login onLoginSuccess={handleLoginSuccess} simpleFetch={simpleFetch} />;
    }

    return (
        <div className="min-h-screen bg-gray-100 p-4 font-inter">
            {/* Muestra mensajes de error si los hay */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
                    <strong className="font-bold">Error:</strong>
                    <span className="block sm:inline ml-2">{error}</span>
                </div>
            )}

            {/* Sección de resumen de registros o formulario de agregar */}
            {!showAddForm ? ( // Condición para mostrar el resumen
                <div className="bg-white shadow-md rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-xl font-semibold text-gray-700">Resumen de Registros</h2>
                        <button
                            onClick={handleLogout}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out"
                        >
                            Cerrar Sesión
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center"> {/* Contenedor flex principal para todos los elementos del resumen */}
                        {/* Total de Registros */}
                        <button
                            onClick={handleClearFilters}
                            className={`p-2 rounded-lg shadow-sm text-left hover:bg-blue-100 transition duration-200 ease-in-out transform hover:scale-105 cursor-pointer flex items-center justify-between min-w-[110px] ${activeFilterType === 'all' ? 'bg-blue-200' : 'bg-blue-50'}`}
                        >
                            <span className="text-xs text-blue-600 font-medium">Total Ingresadas</span>
                            <span className="text-lg font-bold text-blue-800 ml-3">{records.length}</span>
                        </button>
                        {/* Registros Pendientes */}
                        <button
                            onClick={handlePendingFilterClick}
                            className={`p-2 rounded-lg shadow-sm text-left hover:bg-green-100 transition duration-200 ease-in-out transform hover:scale-105 cursor-pointer flex items-center justify-between min-w-[110px] ${activeFilterType === 'pending' ? 'bg-green-200' : 'bg-green-50'}`}
                        >
                            <span className="text-xs text-green-600 font-medium">Pendientes</span>
                            <span className="text-lg font-bold text-green-800 ml-3">
                                {/* Filtra los registros que no están en los estados "finales" */}
                                {records.filter(r => r.estado && !FINAL_ESTADOS.includes(r.estado.toLowerCase())).length}
                            </span>
                        </button>
                        {/* Registros con Deuda */}
                        <button
                            onClick={handleDebtFilterClick}
                            className={`p-2 rounded-lg shadow-sm text-left hover:bg-yellow-100 transition duration-200 ease-in-out transform hover:scale-105 cursor-pointer flex items-center justify-between min-w-[110px] ${activeFilterType === 'debt' ? 'bg-yellow-200' : 'bg-yellow-50'}`}
                        >
                            <span className="text-xs text-yellow-600 font-medium">Con Deuda</span>
                            <span className="text-lg font-bold text-yellow-800 ml-3">
                                {/* Filtra los registros que tienen la palabra 'deuda' en el campo 'deuda' */}
                                {records.filter(r => r.deuda && r.deuda.toLowerCase().includes('deuda')).length}
                            </span>
                        </button>

                        {/* Título "Filtrar por Estado" */}
                        <h3 className="text-lg font-semibold text-gray-700 ml-8 mr-2">Filtrar por Estado:</h3>

                        {/* Resumen por Estado como botones */}
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(stateSummary).map(([estado, count]) => (
                                <button
                                    key={estado}
                                    onClick={() => handleStateFilterClick(estado)}
                                    className={`p-2 rounded-lg shadow-sm flex items-center justify-between min-w-[105px] ${getBadgeColorClass(estado)} hover:opacity-80 transition duration-200 ease-in-out transform hover:scale-105 cursor-pointer ${activeFilterType === 'estado' && filterEstado === estado ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                                >
                                    <span className="text-xs font-medium">{estado}</span>
                                    <span className="text-lg font-bold ml-1">{count}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ) : ( // Condición para mostrar el formulario de agregar
                <div className="bg-white shadow-md rounded-lg p-6 mb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">Agregar Nuevo Registro</h3>

                    {/* Contenedor principal de la cuadrícula para el formulario */}
                    {/* Reducido gap-x para que las columnas estén más juntas */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-4 text-gray-700">
                        {/* Primera Fila, Primera Columna: Número, Año, Bis, Pin */}
                        <div className="flex flex-col">
                            <div className="flex flex-wrap gap-x-2 items-end">
                                <div className="flex flex-col flex-1 min-w-[80px]">
                                    <strong className="text-sm text-gray-600 mb-1">Número:</strong>
                                    <input
                                        type="number"
                                        name="nro"
                                        value={newRecordData.nro || ''}
                                        onChange={handleNewRecordInputChange}
                                        placeholder={newRecordErrors.nro ? 'Campo Obligatorio' : ''}
                                        className={`p-2 border rounded-md w-full transition-all duration-200
                                            ${newRecordErrors.nro
                                                ? 'border-red-500 ring-2 ring-red-500 placeholder-red-400'
                                                : 'border-gray-300 ring-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                                            }
                                        `}
                                    />
                                </div>
                                <div className="flex flex-col flex-1 min-w-[80px]">
                                    <strong className="text-sm text-gray-600 mb-1">Año:</strong>
                                    <input
                                        type="number"
                                        name="anio"
                                        value={newRecordData.anio || ''}
                                        onChange={handleNewRecordInputChange}
                                        className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                                    />
                                </div>
                                <div className="flex flex-col flex-1 min-w-[60px]">
                                    <strong className="text-sm text-gray-600 mb-1">Bis:</strong>
                                    <input
                                        type="number"
                                        name="bis"
                                        value={newRecordData.bis || ''}
                                        onChange={handleNewRecordInputChange}
                                        className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                                    />
                                </div>
                                <div className="flex flex-col flex-1 min-w-[80px]">
                                    <strong className="text-sm text-gray-600 mb-1">Pin:</strong>
                                    <input
                                        type="number"
                                        name="pin"
                                        value={newRecordData.pin || ''}
                                        onChange={handleNewRecordInputChange}
                                        className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Primera Fila, Segunda Columna: Denominación Reservada */}
                        <div className="flex flex-col">
                            <strong className="text-sm text-gray-600 mb-1">Denominación Reservada:</strong>
                            <input
                                type="text"
                                name="denominacion_reservada"
                                value={newRecordData.denominacion_reservada || ''}
                                onChange={handleNewRecordInputChange}
                                placeholder={newRecordErrors.denominacion_reservada ? 'Campo Obligatorio' : ''}
                                className={`p-2 border rounded-md w-full transition-all duration-200
                                    ${newRecordErrors.denominacion_reservada
                                        ? 'border-red-500 ring-2 ring-red-500 placeholder-red-400'
                                        : 'border-gray-300 ring-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                                    }
                                `}
                            />
                        </div>

                        {/* Primera Fila, Tercera Columna: Estado */}
                        <div className="flex flex-col">
                            <strong className="text-sm text-gray-600 mb-1">Estado:</strong>
                            <select
                                name="estado"
                                value={newRecordData.estado || ''}
                                onChange={handleNewRecordInputChange}
                                className={`p-2 border rounded-md w-full transition-all duration-200
                                    ${newRecordErrors.estado
                                        ? 'border-red-500 ring-2 ring-red-500'
                                        : 'border-gray-300 ring-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                                    }
                                `}
                            >
                                <option value="">Seleccionar Estado</option>
                                {uniqueEstados.filter(s => s !== '').map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        {/* Segunda Fila, Primera Columna: Registro */}
                        <div className="flex flex-col">
                            <strong className="text-sm text-gray-600 mb-1">Registro:</strong>
                            <select
                                name="registro" // Nombre del input select
                                value={newRecordData.reg_planca || ''} // Vincular el valor seleccionado a reg_planca
                                onChange={handleNewRecordInputChange}
                                className={`p-2 border rounded-md w-full transition-all duration-200
                                    ${newRecordErrors.registro // Todavía verificar errores en 'registro' ya que es la visualización principal
                                        ? 'border-red-500 ring-2 ring-red-500'
                                        : 'border-gray-300 ring-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                                    }
                                `}
                            >
                                <option value="">Seleccionar Registro</option>
                                {ALL_REGISTROS_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Segunda Fila, Segunda Columna: Oficina Registral */}
                        <div className="flex flex-col">
                            <strong className="text-sm text-gray-600 mb-1">Oficina Registral:</strong>
                            <select
                                name="oficina_registral"
                                value={newRecordData.oficina_registral || ''}
                                onChange={handleNewRecordInputChange}
                                className={`p-2 border rounded-md w-full transition-all duration-200
                                    ${newRecordErrors.oficina_registral
                                        ? 'border-red-500 ring-2 ring-red-500'
                                        : 'border-gray-300 ring-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                                    }
                                `}
                            >
                                <option value="">Seleccionar Oficina Registral</option>
                                {ALL_OFICINAS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        {/* Segunda Fila, Tercera Columna: Botones */}
                        <div className="flex justify-end gap-3 items-end">
                            <button
                                onClick={handleAddRecordSubmit}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                            >
                                Crear Registro
                            </button>
                            <button
                                onClick={closeAddForm}
                                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Sección de la lista de registros */}
            <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Lista de Registros</h2>

                {/* Controles de búsqueda y filtro con el icono de notificación */}
                <div className="flex flex-wrap gap-4 mb-6 relative items-center justify-between">
                    <input
                        type="text"
                        placeholder="Buscar por Número o Denominación..."
                        className="p-2 border border-gray-300 rounded-md flex-grow focus:ring-blue-500 focus:border-blue-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={filterOficina}
                        onChange={(e) => setFilterOficina(e.target.value)}
                    >
                        <option value="">Todas las Oficinas</option>
                        {ALL_OFICINAS.map(oficina => (
                            <option key={oficina} value={oficina}>{oficina}</option>
                        ))}
                    </select>
                    {/* Botón de agregar nuevo registro */}
                    <button
                        onClick={openAddForm}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                    >
                        Agregar Registro
                    </button>

                    {/* Icono de Notificación y Desplegable */}
                    <div className="relative ml-auto notification-container"> {/* Clase añadida notification-container */}
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="relative p-2 rounded-full bg-gray-200 hover:bg-gray-300 transition duration-200 ease-in-out"
                            aria-label="Notificaciones"
                        >
                            {/* Icono de campana SVG */}
                            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0a3 3 0 11-6 0m6 0v1a3 3 0 11-6 0v-1"></path>
                            </svg>
                            {notifications.filter(n => !n.read).length > 0 && (
                                <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full transform translate-x-1/2 -translate-y-1/2">
                                    {notifications.filter(n => !n.read).length}
                                </span>
                            )}
                        </button>

                        {showNotifications && (
                            <div className="absolute right-0 mt-2 w-[550px] bg-white rounded-md shadow-lg z-10 overflow-hidden">
                                <div className="py-1 overflow-y-auto max-h-[700px]">
                                    {notifications.length === 0 ? (
                                        <p className="text-gray-500 px-4 py-2">No hay notificaciones.</p>
                                    ) : (
                                        <>
                                            {notifications.slice().reverse().map(notification => ( // Mostrar todas, las más nuevas primero
                                                <div
                                                    key={notification.id}
                                                    onClick={() => handleNotificationClick(notification.id, notification.recordId)}
                                                    className={`relative group px-4 py-3 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-opacity duration-200 ${
                                                        notification.read ? 'opacity-60' : 'bg-blue-50'
                                                    }`}
                                                >
                                                    <button
                                                        onClick={(e) => handleDeleteNotification(e, notification.id)}
                                                        className="absolute top-2 right-2 p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        aria-label="Eliminar notificación"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                                    </button>
                                                    <p className={`font-semibold text-base pr-6 ${notification.read ? 'text-gray-700' : 'text-gray-900'}`}>
                                                        {notification.type === 'add' && notification.data && (
                                                            <>
                                                                <span className="font-bold">Nuevo registro agregado: </span>
                                                                <span className={getNotificationDataColorClass(notification.type)}>
                                                                    Empresa: {notification.data.denominacion_reservada || 'sin denominación'}. Nro: {notification.data.nro || 'N/A'}.
                                                                </span>
                                                            </>
                                                        )}
                                                        {notification.type === 'update' && notification.data && (
                                                            <>
                                                                <span className="font-bold">Registro actualizado: </span>
                                                                <span className={getNotificationDataColorClass(notification.type)}>
                                                                    Empresa: {notification.data.denominacion_reservada || 'sin denominación'}. Nro: {notification.data.nro || 'N/A'}.
                                                                </span>
                                                                {(notification.data.stateChanged || notification.data.observationChanged || notification.data.planchaChanged) && <br />} {/* Nueva línea si hay cambios */}
                                                                {notification.data.stateChanged && (
                                                                    <>
                                                                        <span className="font-bold"> El Estado cambió a: </span>
                                                                        <span className={getNotificationDataColorClass(notification.type)}>"{notification.data.newEstado || 'N/A'}". </span>
                                                                    </>
                                                                )}
                                                                {notification.data.observationChanged && (
                                                                    <>
                                                                        <span className="font-bold"> La Observación cambió. </span>
                                                                    </>
                                                                )}
                                                                {notification.data.planchaChanged && notification.data.hasPlancha && (
                                                                    <>
                                                                        <span className="font-bold"> ¡Plancha disponible! </span>
                                                                        <span className={getNotificationDataColorClass(notification.type)}>El archivo de plancha fue descargado.</span>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                        {notification.type === 'delete' && notification.data && (
                                                            <>
                                                                <span className="font-bold">Registro eliminado: </span>
                                                                <span className={getNotificationDataColorClass(notification.type)}>ID {notification.data.id}.</span>
                                                            </>
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        {new Date(notification.timestamp).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                                                    </p>
                                                </div>
                                            ))}
                                            <div className="border-t border-gray-200 mt-1">
                                                <button
                                                    onClick={() => setNotifications(prevNotifs => prevNotifs.map(n => ({ ...n, read: true })))}
                                                    className="w-full text-center py-2 text-sm text-blue-600 hover:bg-blue-50"
                                                >
                                                    Marcar todas como leídas
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Indicador de carga o tabla de registros */}
                {loading ? (
                    <div className="flex items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                        <p className="ml-4 text-gray-700">Cargando registros...</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto overflow-y-scroll rounded-lg border border-gray-200 shadow-sm max-h-[calc(100vh-350px)]">
                        <table className="min-w-full divide-y divide-gray-200 table-fixed">
                            <thead className="bg-gray-50">
                                <tr>
                                    {TABLE_COLUMNS.map(column => (
                                        <th
                                            key={column.key}
                                            className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer`}
                                            style={{ width: column.width }}
                                            onClick={() => handleSort(column.key)}
                                        >
                                            {column.label} {sortColumn === column.key && (sortDirection === 'asc' ? '▲' : '▼')}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredAndSortedRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan={TABLE_COLUMNS.length} className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                            No se encontraron registros que coincidan con los criterios.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredAndSortedRecords.map((record) => (
                                        <React.Fragment key={record.id}>
                                            <tr ref={el => rowRefs.current[record.id] = el} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(record)}>
                                                {TABLE_COLUMNS.map(column => (
                                                    <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 overflow-hidden text-ellipsis">
                                                        {column.key === 'estado' ? (
                                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getBadgeColorClass(record.estado)}`}>
                                                                {record.estado || 'N/A'}
                                                            </span>
                                                        ) : ['ingresado', 'ult_update', 'fecha_desc_planca', 'vence'].includes(column.key) ? (
                                                            formatDate(record[column.key])
                                                        ) : (
                                                            record[column.key] || ''
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            {/* Esta fila siempre estará en el DOM, pero su contenido se expandirá/contraerá */}
                                            <tr className="bg-gray-50">
                                                <td colSpan={TABLE_COLUMNS.length} className="p-0"> {/* Quitar padding del TD para que la transición de padding sea en el div interno */}
                                                    <div
                                                        className={`bg-white shadow-inner rounded-lg border border-gray-200 overflow-hidden transition-all duration-300 ease-in-out
                                                            ${expandedRowId === record.id ? 'max-h-[500px] opacity-100 p-4' : 'max-h-0 opacity-0 p-0'}
                                                        `}
                                                        // max-h-[500px] es un valor de ejemplo, ajusta si el contenido es mucho más grande
                                                    >
                                                        {/* Contenido existente de la fila expandida */}
                                                        <h4 className="text-lg font-semibold text-gray-700 mb-3">
                                                            {isEditing ? `Editando Registro: ${record.denominacion_reservada || record.nro}` : `EMPRESA: ${record.denominacion_reservada || record.nro}`}
                                                        </h4>
                                                        {/* Changed grid-cols-4 to grid-cols-6 */}
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2 text-gray-700">
                                                            {DETAIL_FIELDS.map(key => {
                                                                // Usar editedRecord de forma segura, estableciendo un objeto vacío si es null
                                                                const currentEditedRecord = editedRecord || {};

                                                                // Definimos los campos que NO serán editables Y NO se mostrarán en modo edición
                                                                const hiddenInEditModeFields = ['deuda', 'vence', 'monto', 'observacion', 'escribano', 'ult_update', 'fecha_desc_planca', 'reg_planca'];
                                                                // Definimos los campos que NO serán editables pero SÍ se mostrarán
                                                                const nonEditableButVisibleFields = ['id', 'ctid'];

                                                                const isEditableField = !hiddenInEditModeFields.includes(key) && !nonEditableButVisibleFields.includes(key);

                                                                // Si estamos en modo edición y el campo debe estar oculto, no lo renderizamos
                                                                if (isEditing && hiddenInEditModeFields.includes(key)) {
                                                                    return null;
                                                                }

                                                                const inputType = ['ingresado', 'fecha_desc_planca', 'vence'].includes(key) ? 'date' : 'text';

                                                                // Acceder a currentEditedRecord[key]
                                                                const dateValue = currentEditedRecord[key];
                                                                const dateObj = new Date(dateValue);
                                                                const formattedDateForInput = inputType === 'date' && dateValue && !isNaN(dateObj.getTime())
                                                                    ? dateObj.toISOString().split('T')[0]
                                                                    : '';
                                                                const inputValue = inputType === 'date' ? formattedDateForInput : (currentEditedRecord[key] || '');

                                                                return (
                                                                    <div
                                                                        key={key}
                                                                        className={`flex flex-col ${['escribano', 'observacion'].includes(key) ? 'lg:col-span-2' : ''}`}
                                                                    >
                                                                        <strong className="text-sm text-gray-600 mb-1">{formatLabel(key)}:</strong>
                                                                        {isEditing && isEditableField ? (
                                                                            key === 'estado' ? (
                                                                                <select
                                                                                    name={key}
                                                                                    value={inputValue || ''}
                                                                                    onChange={handleInputChange}
                                                                                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                                                >
                                                                                    <option value="">Seleccionar Estado</option>
                                                                                    {uniqueEstados.filter(s => s !== '').map(estado => (
                                                                                        <option key={estado} value={estado}>{estado}</option>
                                                                                    ))}
                                                                                </select>
                                                                            ) : key === 'oficina_registral' ? (
                                                                                <select
                                                                                    name={key}
                                                                                    value={inputValue || ''}
                                                                                    onChange={handleInputChange}
                                                                                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                                                >
                                                                                    <option value="">Seleccionar Oficina</option>
                                                                                    {ALL_OFICINAS.map(oficina => (
                                                                                        <option key={oficina} value={oficina}>{oficina}</option>
                                                                                    ))}
                                                                                </select>
                                                                            ) : key === 'registro' ? (
                                                                                // Manejar 'registro' (etiqueta) con el select
                                                                                <select
                                                                                    name={key} // Usar 'key' como nombre para distinguir qué campo se está editando
                                                                                    value={currentEditedRecord.reg_planca || ''} // Vincular a reg_planca para la selección
                                                                                    onChange={handleInputChange}
                                                                                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                                                >
                                                                                    <option value="">Seleccionar Tipo de Registro</option>
                                                                                    {ALL_REGISTROS_OPTIONS.map(registroOption => (
                                                                                        <option key={registroOption.value} value={registroOption.value}>{registroOption.label}</option>
                                                                                    ))}
                                                                                </select>
                                                                            ) : (
                                                                                <input
                                                                                    type={inputType}
                                                                                    name={key}
                                                                                    value={inputValue || ''}
                                                                                    onChange={handleInputChange}
                                                                                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                                                />
                                                                            )
                                                                        ) : (
                                                                            <span className="text-base">
                                                                                {/* Lógica de visualización para campos no editables o en modo de solo lectura */}
                                                                                {key === 'deuda' ? (
                                                                                    record[key] === null ? (
                                                                                        <span className="text-sm font-medium text-gray-500">
                                                                                            Pendiente de verificación
                                                                                         </span>
                                                                                    ) : record[key] === '' ? (
                                                                                        <span className="text-sm font-medium text-green-700">
                                                                                            No tiene deuda
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="text-sm font-medium text-red-700">
                                                                                            {record[key]}
                                                                                        </span>
                                                                                    )
                                                                                ) : key === 'observacion' ? (
                                                                                    record[key] === null || record[key] === '' ? (
                                                                                        <span className="text-sm font-medium text-gray-500">
                                                                                            Sin observación
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="text-sm font-medium text-orange-700">
                                                                                            {record[key]}
                                                                                        </span>
                                                                                    )
                                                                                ) : key === 'ult_update' || key === 'fecha_desc_planca' ? (
                                                                                    formatDate(record[key]) || '' // Mostrar vacío si es null/vacío
                                                                                ) : key === 'vence' ? (
                                                                                    record[key] ? ( // Ahora esto se evaluará incluso si no hay deuda
                                                                                        <span className="text-sm font-medium text-red-700">
                                                                                            {formatDate(record[key])}
                                                                                        </span>
                                                                                    ) : (
                                                                                        '' // Mostrar vacío si no hay valor
                                                                                    )
                                                                                ) : key === 'monto' ? (
                                                                                    record[key] ? ( // Ahora esto se evaluará incluso si no hay deuda
                                                                                        <span className="text-sm font-medium text-red-700">
                                                                                            {new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }).format(record[key])}
                                                                                        </span>
                                                                                    ) : (
                                                                                        '' // Mostrar vacío si no hay valor
                                                                                    )
                                                                                ) : (
                                                                                    record[key] || ''
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        {/* Botones de acción dentro de la fila expandida */}
                                                        <div className="mt-6 flex justify-end gap-3 flex-wrap">
                                                            {!isEditing ? ( // Mostrar estos botones solo cuando NO se está editando
                                                                <>
                                                                    {record.deuda && record.ruta_deuda && (
                                                                        <button
                                                                            onClick={() => handleDownload('pdf_deuda')}
                                                                            className="bg-indigo-800 hover:bg-indigo-900 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                                                        >
                                                                            Descargar Deuda
                                                                        </button>
                                                                    )}
                                                                    {record.ruta_screenshot && (
                                                                        <button
                                                                            onClick={() => handleDownload('screenshot')}
                                                                            className="bg-purple-800 hover:bg-purple-900 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                                                        >
                                                                            Descargar Observación
                                                                        </button>
                                                                    )}
                                                                    {record.plancha && ( // Renderizar condicionalmente el botón 'Ver Plancha'
                                                                        <button
                                                                            onClick={() => handleDownload('plancha')}
                                                                            className="bg-teal-800 hover:bg-teal-900 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                                                        >
                                                                            Descargar Plancha
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={handleEditClick}
                                                                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                                                    >
                                                                        Editar Registro
                                                                    </button>
                                                                    <button
                                                                        onClick={handleDelete}
                                                                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                                                    >
                                                                        Eliminar Registro
                                                                    </button>
                                                                </>
                                                            ) : ( // Mostrar estos botones solo cuando se está EDITANDO
                                                                <>
                                                                    <button
                                                                        onClick={handleSave}
                                                                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                                                    >
                                                                        Guardar Cambios
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setIsEditing(false)}
                                                                        className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                                                                    >
                                                                        Cancelar
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Confirmación (Personalizado) */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-auto p-6 relative">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">
                            {confirmAction ? 'Confirmar Acción' : 'Información'}
                        </h3>
                        <p className="text-gray-700 mb-6 whitespace-pre-line">{actionMessage}</p>
                        <div className="flex justify-end gap-3">
                            {confirmAction ? (
                                <>
                                    <button
                                        onClick={() => setShowConfirmModal(false)}
                                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (confirmAction) {
                                                confirmAction();
                                                setShowConfirmModal(false); // Cerrar modal después de confirmar
                                            }
                                        }}
                                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                                >
                                    Confirmar
                                </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setShowConfirmModal(false)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                                >
                                    Cerrar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Componente de Login ---
const Login = ({ onLoginSuccess, simpleFetch }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!username || !password) {
            setError('Por favor, ingrese usuario y contraseña.');
            setLoading(false);
            return;
        }

        try {
            const response = await simpleFetch('/api/login', {
                method: 'POST',
                body: JSON.stringify({ username, password }),
            }, true); // El 'true' indica que es una petición de login y no debe enviar token

            const data = await response.json();
            onLoginSuccess(data.access_token, data.user);

        } catch (err) {
            console.error("Error de login:", err);
            setError(err.message || 'Error al iniciar sesión. Verifique sus credenciales.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Iniciar Sesión</h2>
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        {error}
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">
                            Usuario
                        </label>
                        <input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        />
                    </div>
                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                            Contraseña
                        </label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full disabled:bg-blue-300"
                        >
                            {loading ? 'Ingresando...' : 'Ingresar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default App;
